/**
 * A-MODUL-NAV1 (PASS 001699) — Zuordnung Menuepunkt/Route -> Modul an EINER
 * Stelle.
 *
 * Owner 05.10.: Basis (Flotte, Karte, Fahrten) ist immer sichtbar; einzeln
 * verkauft werden Wartung, Tank & Kosten, Berichte und Genius Intelligence.
 * Video und Clean City kommen spaeter.
 *
 * Diese Datei ist der einzige Ort, der Pfade auf Module abbildet. Sowohl die
 * Shell-Navigation (Menuefilter) als auch der Routenwaechter lesen hier — so
 * kann das Menue nie einen Link anbieten, den der Waechter danach blockiert.
 *
 * Fail-open mit Absicht: jeder Pfad ohne Treffer gehoert zum Basis-Modul und
 * bleibt damit sichtbar. Ein neuer Pfad verschwindet also nie still aus dem
 * Menue; er muss hier bewusst einem verkauften Modul zugeordnet werden. Die
 * Schreibsperren macht ohnehin der Server (Lane C), nicht der Client.
 *
 * Spiegel von `services/api/src/modules/billing-modules/catalog.ts` und
 * `effective-modules.ts`. Der Client teilt keinen Code mit `services/api`
 * (getrennte tsconfig-Projekte), deshalb sind die Listen hier bewusst
 * dupliziert und werden vom Vertragstest gegen den Endpunkt gehalten.
 */

export const BILLING_MODULES = [
  "basis",
  "maintenance",
  "fuel_costs",
  "reports",
  "genius_intelligence",
] as const;

export type BillingModule = (typeof BILLING_MODULES)[number];

/** Basis wird nicht verkauft und ist immer aktiv (Owner 05.10.). */
export const BASE_MODULE: BillingModule = "basis";

/** Die einzeln verkauften Module — Basis ausgenommen. */
export const SELLABLE_MODULES: ReadonlyArray<BillingModule> = BILLING_MODULES.filter(
  (m) => m !== BASE_MODULE,
);

export type ModuleState = "active" | "read_only" | "hidden";

export function isBillingModule(value: string): value is BillingModule {
  return (BILLING_MODULES as ReadonlyArray<string>).includes(value);
}

/**
 * Pfad-Praefix -> Modul. Laengster Treffer gewinnt, damit ein spaeterer
 * Unterpfad (`/reports/schedules`) notfalls ein eigenes Modul bekommen kann,
 * ohne die Reihenfolge der Liste zu bedeuten.
 *
 * `/insights` und `/genius/*` gehoeren zu Genius Intelligence; `/calendar` ist
 * der Wartungskalender und folgt deshalb der Wartung.
 */
export const MODULE_ROUTE_PREFIXES: ReadonlyArray<{
  readonly prefix: string;
  readonly module: BillingModule;
}> = [
  { prefix: "/maintenance", module: "maintenance" },
  { prefix: "/calendar", module: "maintenance" },
  { prefix: "/fuel", module: "fuel_costs" },
  { prefix: "/reports", module: "reports" },
  { prefix: "/intelligence", module: "genius_intelligence" },
  { prefix: "/genius", module: "genius_intelligence" },
  { prefix: "/insights", module: "genius_intelligence" },
];

/**
 * Modul eines Pfades oder eines Routen-Patterns. Query-Strings werden
 * abgeschnitten (`/reports?group=fuel` -> `/reports`), damit Menueziele und
 * Routen-Patterns dieselbe Antwort liefern.
 */
export function moduleForPath(pathOrPattern: string): BillingModule {
  const path = (pathOrPattern.split("?")[0] ?? pathOrPattern).trim();
  let best: { prefix: string; module: BillingModule } | undefined;
  for (const entry of MODULE_ROUTE_PREFIXES) {
    if (path === entry.prefix || path.startsWith(`${entry.prefix}/`)) {
      if (!best || entry.prefix.length > best.prefix.length) best = entry;
    }
  }
  return best?.module ?? BASE_MODULE;
}

/**
 * Minimalform eines Menuepunkts, damit diese Datei nicht von `navModel.ts`
 * abhaengt (`navModel` darf hier importieren, nicht umgekehrt).
 */
export interface ModuleBoundNavItem {
  readonly to?: string | undefined;
}

/**
 * Modul eines Menuepunkts. Das Modul folgt dem Routenziel, nicht der
 * Menuegruppe: `fuel-reports` steht in der Tank-Gruppe, zielt aber auf
 * `/reports` und folgt deshalb dem Berichte-Modul. Andernfalls stuende ein
 * Menuepunkt sichtbar da, dessen Route der Waechter sperrt.
 *
 * Punkte ohne Ziel (Vorschau-/externe Eintraege) gehoeren zur Basis.
 */
export function moduleForNavItem(item: ModuleBoundNavItem): BillingModule {
  if (!item.to) return BASE_MODULE;
  return moduleForPath(item.to);
}

/**
 * Ziel des Admin-Hinweises "Module und Paket". Die Seite selbst baut Lane D
 * (PASS 001693); bis dahin zeigt der Hinweis auf den Admin-Hub. EIN Konstante,
 * damit die Umstellung spaeter an einer Stelle passiert.
 */
export const MODULE_PACKAGE_ADMIN_PATH = "/admin-hub";

/** i18n-Schluessel des Modulnamens — gleiche Form wie im Server-Katalog. */
export function moduleLabelKey(module: BillingModule): string {
  return `modules.${module}.name`;
}
